/* eslint-disable prettier/prettier */
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { 
  StepContainer, 
  StepTitle, 
  SummaryContainer,
  SummarySection,
  SummarySectionTitle,
  SummaryItem
} from './styles';

const Confirmacao = ({ formValues, onGenerateLink }) => {
  const isBatchMode = Boolean(formValues?.batch_mode);
  const batchCustomers = formValues?.batch_customers || [];
  const batchHasMultipleActivities = new Set(
    batchCustomers
      .map((customer) => String(customer?.service_name || '').trim())
      .filter(Boolean)
  ).size > 1;
  const batchHasActivityPerCustomer = batchCustomers.some((customer) => String(customer?.service_name || '').trim());

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatCPF = (cpf) => {
    if (!cpf) return 'Não informado';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Não informado';
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const getRecurrenceLabel = () => {
    const labels = {
      single: 'Pagamento único',
      weekly: 'Semanal',
      biweekly: 'Quinzenal',
      monthly: 'Mensal',
      bimonthly: 'Bimestral',
      quarterly: 'Trimestral',
      semiannual: 'Semestral',
      annual: 'Anual',
    };
    return labels[formValues?.recurrence_type] || 'Pagamento único';
  };

  const getPaymentMethodsText = () => {
    if (!formValues?.payment_methods || formValues.payment_methods.length === 0) {
      return 'Nenhum método selecionado';
    }
    
    const methods = {
      boleto: 'Boleto',
      cartao: 'Cartão (crédito/débito)',
      pix: 'PIX'
    };
    
    return formValues.payment_methods.map(m => methods[m]).join(', ');
  };

  return (
    <StepContainer>
      <StepTitle>Confirmação</StepTitle>
      
      <SummaryContainer>
        <SummarySection>
          <SummarySectionTitle>Dados do Cliente</SummarySectionTitle>
          <SummaryItem>
            <strong>Atividade/Serviço:</strong>
            <span>
              {isBatchMode
                ? batchHasMultipleActivities
                  ? 'Definida por cliente no arquivo'
                  : formValues?.service_name || batchCustomers[0]?.service_name || 'Não informado'
                : formValues?.service_name || 'Não informado'}
            </span>
          </SummaryItem>
          {isBatchMode ? (
            <>
              <SummaryItem>
                <strong>Modo:</strong>
                <span>Geração em massa</span>
              </SummaryItem>
              {batchHasActivityPerCustomer && (
                <SummaryItem>
                  <strong>Atividade por cliente:</strong>
                  <span>Usando a coluna atividade do arquivo com fallback para a atividade padrão</span>
                </SummaryItem>
              )}
              <SummaryItem>
                <strong>Clientes importados:</strong>
                <span>{batchCustomers.length}</span>
              </SummaryItem>
              <SummaryItem>
                <strong>Envio automático:</strong>
                <span>Após confirmar, os links serão enviados por e-mail e WhatsApp quando os dados estiverem preenchidos</span>
              </SummaryItem>
            </>
          ) : (
            <>
              <SummaryItem>
                <strong>Nome do cliente:</strong>
                <span>{formValues?.customer_name || 'Não informado'}</span>
              </SummaryItem>
              <SummaryItem>
                <strong>CPF do cliente:</strong>
                <span>{formatCPF(formValues?.customer_cpf)}</span>
              </SummaryItem>
              <SummaryItem>
                <strong>E-mail do cliente:</strong>
                <span>{formValues?.customer_email || 'Não informado'}</span>
              </SummaryItem>
              <SummaryItem>
                <strong>Telefone do cliente:</strong>
                <span>{formatPhone(formValues?.customer_phone)}</span>
              </SummaryItem>
            </>
          )}
        </SummarySection>

        {formValues?.has_partner && (
          <SummarySection>
            <SummarySectionTitle>Dados do Parceiro</SummarySectionTitle>
            <SummaryItem>
              <strong>Parceiro:</strong>
              <span>{formValues?.partner_name || 'Não informado'}</span>
            </SummaryItem>
            <SummaryItem>
              <strong>CNPJ:</strong>
              <span>{formValues?.partner_cnpj || 'Não informado'}</span>
            </SummaryItem>
            <SummaryItem>
              <strong>E-mail:</strong>
              <span>{formValues?.partner_email || 'Não informado'}</span>
            </SummaryItem>
            <SummaryItem>
              <strong>Instituição Financeira:</strong>
              <span>{formValues?.partner_bank || 'Não informado'}</span>
            </SummaryItem>
            <SummaryItem>
              <strong>Conta:</strong>
              <span>{formValues?.partner_account || 'Não informado'}</span>
            </SummaryItem>
          </SummarySection>
        )}

        <SummarySection>
          <SummarySectionTitle>Valores</SummarySectionTitle>
          <SummaryItem>
            <strong>Valor bruto:</strong>
            <span>{formatCurrency(formValues?.link_value)}</span>
          </SummaryItem>
          <SummaryItem>
            <strong>Valor líquido:</strong>
            <span>{formatCurrency((formValues?.link_value || 0) * 0.95)}</span>
          </SummaryItem>
          {formValues?.has_partner && (
            <>
              <SummaryItem>
                <strong>Percentual estação:</strong>
                <span>{formValues?.station_percentage || 0}%</span>
              </SummaryItem>
              <SummaryItem>
                <strong>Percentual parceiro:</strong>
                <span>{formValues?.partner_percentage || 0}%</span>
              </SummaryItem>
            </>
          )}
        </SummarySection>

        <SummarySection>
          <SummarySectionTitle>Forma de Pagamento</SummarySectionTitle>
          <SummaryItem>
            <strong>Tipo de pagamento:</strong>
            <span>{getRecurrenceLabel()}</span>
          </SummaryItem>
          <SummaryItem>
            <strong>Métodos disponíveis:</strong>
            <span>{getPaymentMethodsText()}</span>
          </SummaryItem>
          <SummaryItem>
            <strong>Permite parcelamento?</strong>
            <span>{formValues?.allow_installments ? 'Sim' : 'Não'}</span>
          </SummaryItem>
        </SummarySection>

        {(formValues?.custom_message || '').trim() && (
          <SummarySection>
            <SummarySectionTitle>Mensagem personalizada</SummarySectionTitle>
            <SummaryItem>
              <strong>Mensagem:</strong>
              <span style={{ maxWidth: '60%', textAlign: 'right', whiteSpace: 'pre-wrap' }}>
                {formValues.custom_message}
              </span>
            </SummaryItem>
          </SummarySection>
        )}
      </SummaryContainer>

      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onGenerateLink}
          style={{ 
            padding: '12px 48px',
            fontSize: '16px',
            textTransform: 'none'
          }}
        >
          {isBatchMode ? 'Gerar links em massa' : 'Gerar link'}
        </Button>
      </div>
    </StepContainer>
  );
};

Confirmacao.propTypes = {
  formValues: PropTypes.object,
  onGenerateLink: PropTypes.func.isRequired,
};

export default Confirmacao;
